import React, {useCallback, useContext, useEffect, useState} from "react";
import * as PropTypes from "prop-types";
import * as ROUTES from "../../../common/routes";
import {useKeycloakFunctions} from "../../../hooks";
import {LOCAL} from "../../../common/constant";
import {LocaleContext} from "../../../store/providers/LocaleProvider";
import Route from "./Route";

const PrivateRoute = ({
    component: Component,
    roles,
    path,
    redirectPath,
    exact,
    seoTitle,
    ...props
}) => {

    const {keycloak, initialized, isAdmin, tokenParsed, authenticated, login} = useKeycloakFunctions();
    const {locale} = useContext(LocaleContext)[0];
    const [hasAccess, setAccess] = useState();

    /*
     *   This function checks if required roles to enter
     *   current private route exist in user's access token
     *   payload. All roles should exist in the token in
     *   order for the user to be authorized to access
     *   the specific route.
     */
    const isAuthorized = useCallback(async (roles) => {
        if (initialized) {
            for (const roleGroup of roles) {
                let userHasAllRolesInGroup = true;

                for (const role of roleGroup) {
                    if (!(keycloak.hasRealmRole(role) || keycloak.hasResourceRole(role))) {
                        userHasAllRolesInGroup = false;
                    }
                }

                if (userHasAllRolesInGroup) {
                    return true;
                }
            }

            return false;
        }
    }, [keycloak, tokenParsed]);

    const validateRoleAccess = useCallback(async () => {
        if (roles?.length && !await isAuthorized(roles)) {
            window.location.href = "/" + locale + redirectPath;
            return false;
        }
        return true;
    }, [isAuthorized]);

    useEffect(() => {
        /*  Access checks
         *   0. Check if session expired and idle dialog is shown
         *   1. Check if user is authenticated
         *   2. Admin or localhost -> always authorized
         *   3. All roles feeded should exist in token
         */
        const url = new URL(window.location.href);
        const displayIdleDialog = url.searchParams.get("displayIdleDialog");
        if(displayIdleDialog === "true") {
            setAccess(true);
        } else if(initialized && !authenticated) {
            login();
        } else if (isAdmin() || location.host.includes(LOCAL)) {
            setAccess(true);
        } else {
            validateRoleAccess().then(res => {
                setAccess(res);
            });
        }
    }, [location.href, initialized, authenticated]);

    return (
        <>
            {hasAccess ? (
                <Route
                    {...props}
                    exact={exact}
                    path={path}
                    render={(props) => <Component {...props}/>}
                    isPrivate
                    seoTitle={seoTitle}
                />
            ) : null}
        </>
    );
};

PrivateRoute.defaultProps = {
    redirectPath: ROUTES.ERROR_PAGE_404,
    exact: true
};

PrivateRoute.propTypes = {
    path: PropTypes.string.isRequired,
    component: PropTypes.any,
    roles: PropTypes.array,
    redirectPath: PropTypes.string,
    exact: PropTypes.bool,
    seoTitle: PropTypes.string
};

export default PrivateRoute;
